import { Box, Grid, Card, Link } from '@mui/material'
import { styled } from '@mui/material/styles'
import moment from 'moment'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppState } from 'appContext'
import { PasswordModal } from 'components/PasswordModal'
import { useAllApplications } from 'hooks'
import { useIsSmallScreen, useUrlGenerator } from 'packages/core'
import { PaddedDiv } from 'components/ComputerSessionRequest/SessionRequestTile'
import classNames from 'classnames'
import { Avatar } from 'packages/eid-ui'
import { getFullImageUrl } from 'utils'
const smallScreenWidth = '1022px'

export interface CredentialCheckoutTileProps {
    item: any
    parent: any
}

const RequestMainContainer = styled(Box)(({ theme }) => ({
    borderRadius: '0.8rem',
    boxShadow: '0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.05)',
    border: 'solid 0.1rem rgba(0, 0, 0, 0.11)',
    background: theme?.palette?.background?.paper,
    marginTop: '1.7rem',
    paddingBottom: '2rem',
    overflow: 'hidden',
}))

const RequestHeaderTitle = styled(Box)({
    fontSize: '1.4rem',
    fontWeight: 600,
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
})

const RequestHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 'solid 0.1rem rgba(0, 0, 0, 0.11)',
    padding: '0.4rem 2rem',
    backgroundImage:
        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
    backgroundSize: '0.8rem 0.8rem',
})

const RequestHeaderTitleButton = styled(Box)(({ theme }) => ({
    '& button': {
        fontSize: '1.1rem',
        color: theme?.palette?.common?.white,
        background: theme?.palette?.secondary?.main,
        borderRadius: '0.5rem',
        padding: '0.1rem 0.4rem 0.1rem',
        textTransform: 'capitalize',
        '&:hover': {
            background: theme?.palette?.secondary?.main,
        },
    },
}))

const RequestItemShow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 2rem',
    marginTop: '0.7rem',
    [`@media (max-width:${smallScreenWidth})`]: {
        flexDirection: 'column',
        alignItems: 'start',
        gap: '1.6rem',
    },
})

const RequestLeftShow = styled(Box)({
    // TODO: Commenting until we get new design
    // width: '25%',
})

const RequestItemTitle = styled(Box)({
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#9b9b9b',
    margin: '0rem',
    textTransform: 'uppercase',
    [`@media (max-width:${smallScreenWidth})`]: {
        fontWeight: '600',
    },
})

const RequestItemHeading = styled(Box)({
    fontSize: '1.4rem',
    fontWeight: 500,
    margin: '0rem',
})

const RequestItemHeadingLink = styled(RequestItemHeading)(({ theme }) => ({
    '& a': {
        color: `${theme?.palette?.primary?.main} !important`,
        textDecoration: 'none !important',
    },
}))

const RequestItemTime = styled(Box)(({ theme }) => ({
    fontSize: '1.4rem',
    fontWeight: 500,
    color: theme?.palette?.common?.black,
    margin: '0rem',
    '& button': {
        fontSize: '1.1rem',
        borderRadius: '0.5rem',
        padding: '0.1rem 0.4rem 0.1rem',
        textTransform: 'capitalize',
        '&:hover': {
            background: theme?.palette?.secondary?.main,
        },
    },
}))

const CheckInButton = styled('button')(({ theme }) => ({
    fontSize: '1.1rem',
    color: theme?.palette?.common?.white,
    background: theme?.palette?.primary?.main,
    borderRadius: '0.5rem',
    '&:hover': {
        background: theme?.palette?.primary?.main,
    },
}))

const AvtarSection = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    ' & > div': {
        height: '2rem',
        width: '2rem',
    },
})

const AvtarName = styled('span')({
    marginLeft: '0.5rem',
})

const RequestConnectBtn = styled(Box)({
    ' & button': {
        padding: '0',
        ' & div': {
            maxWidth: 'auto !important',
        },
    },
})

export const CredentialCheckoutTile: FC<CredentialCheckoutTileProps> = (
    props,
) => {
    const { item } = props
    const [{ masterPassword }]: any = useAppState()
    const [open, setOpen] = useState('')
    const { t } = useTranslation()
    const { getMyTasksUrl } = useUrlGenerator(useAllApplications)
    const isSmallScreen = useIsSmallScreen()
    const myTasksBusinessRequestUrl = getMyTasksUrl(
        `/myRequests/businessRequests?id=${item.businessRequestId}`,
    )
    const myTasksBusinessRequestItemUrl = getMyTasksUrl(
        `/myRequests/businessRequestItems?id=${item.businessRequestItemId}`,
    )

    return (
        <>
            {!masterPassword && (
                <PasswordModal
                    showDefaultContent={false}
                    open={open}
                    setOpen={setOpen}
                />
            )}

            <PaddedDiv isSmallScreen={isSmallScreen}>
                <Box
                    sx={{
                        borderImageSource:
                            'linear-gradient(to right, rgba(1, 174, 143, 0), #04ab936e 7%, #2c83bd47 91%, rgba(48, 127, 193, 0))',
                        borderImageSlice: '1',
                        borderBottomWidth: '1px',
                        borderStyle: 'solid',
                        borderTopWidth: '0',
                        overflow: 'hidden',
                    }}
                >
                    <RequestMainContainer>
                        <RequestHeader>
                            <RequestHeaderTitle as="h2">
                                {item.credentialFriendlyName
                                    ? item.credentialFriendlyName
                                    : '---'}
                            </RequestHeaderTitle>
                        </RequestHeader>
                        <RequestItemShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_BusinessRequest')}
                                </RequestItemTitle>
                                {item?.businessRequestId &&
                                item.businessRequestName ? (
                                    <RequestItemHeadingLink as="h2">
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={myTasksBusinessRequestUrl}
                                        >
                                            {item.businessRequestName
                                                ? item.businessRequestName
                                                : '---'}
                                        </a>
                                    </RequestItemHeadingLink>
                                ) : (
                                    <RequestItemHeading as="h2">
                                        {item.businessRequestName
                                            ? item.businessRequestName
                                            : '---'}
                                    </RequestItemHeading>
                                )}
                            </RequestLeftShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_BusinessRequestItem')}
                                </RequestItemTitle>
                                {item?.businessRequestItemId &&
                                item.businessRequestItemName ? (
                                    <RequestItemHeadingLink as="h2">
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={myTasksBusinessRequestItemUrl}
                                        >
                                            {item.businessRequestItemName
                                                ? item.businessRequestItemName
                                                : '---'}
                                        </a>
                                    </RequestItemHeadingLink>
                                ) : (
                                    <RequestItemHeading as="h2">
                                        {item.businessRequestItemName
                                            ? item.businessRequestItemName
                                            : '---'}
                                    </RequestItemHeading>
                                )}
                            </RequestLeftShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_RequestedBy')}
                                </RequestItemTitle>
                                <AvtarSection>
                                    <Avatar
                                        size="small"
                                        src={getFullImageUrl(
                                            item.requestedByPersonImageThumbUrl,
                                        )}
                                    />
                                    <AvtarName>
                                        {item.requestorFriendlyName
                                            ? item.requestorFriendlyName
                                            : '---'}
                                    </AvtarName>
                                </AvtarSection>
                            </RequestLeftShow>
                        </RequestItemShow>

                        <RequestItemShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_StartTime')}
                                </RequestItemTitle>
                                <RequestItemTime as="h2">
                                    {item.startTimeUtc
                                        ? moment
                                              .utc(item.startTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </RequestItemTime>
                            </RequestLeftShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_EndTime')}
                                </RequestItemTitle>
                                <RequestItemTime as="h2">
                                    {item.endTimeUtc
                                        ? moment
                                              .utc(item.endTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </RequestItemTime>
                            </RequestLeftShow>

                            <Box>
                                <RequestItemTitle as="h2">
                                    {t('Common_ActualCheckOutTime')}
                                </RequestItemTitle>
                                <RequestItemTime as="h2">
                                    {item.userCheckedOutTimeUtc
                                        ? moment
                                              .utc(item.userCheckedOutTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </RequestItemTime>
                            </Box>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_ActualCheckInTime')}
                                </RequestItemTitle>
                                <RequestItemTime as="h2">
                                    {item.userCheckedInTimeUtc
                                        ? moment
                                              .utc(item.userCheckedInTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </RequestItemTime>
                            </RequestLeftShow>
                        </RequestItemShow>
                    </RequestMainContainer>
                </Box>
            </PaddedDiv>
        </>
    )
}

export default CredentialCheckoutTile
